<template>
  <fullscreen-dialog
    v-model="model"
    :title="receiptTitle"
    icon="mdi-truck-check"
    color="primary"
    hide-footer
    :loading="loading"
  >
    <template v-slot:app-bar-actions>
      <print-button @click="print" />
    </template>

    <v-form ref="form" v-model="valid">
      <div class="receipt-layout">
        <div class="receipt-top-row">
          <v-card outlined>
            <v-card-title>
              <v-row justify="space-between" align="center" no-gutters>
                <span>{{ receiptTitle }}</span>

                <v-chip
                  label
                  :color="allDelivered ? 'green lighten-4' : 'orange lighten-4'"
                >
                  <v-icon
                    small
                    :color="allDelivered ? 'green darken-2' : 'orange darken-2'"
                    class="mr-2"
                  >
                    {{ allDelivered ? "mdi-check-circle" : "mdi-alert" }}
                  </v-icon>
                  <span
                    :class="
                      allDelivered
                        ? 'green--text text--darken-2'
                        : 'orange--text text--darken-2'
                    "
                    >{{ statusText }}</span
                  >
                </v-chip>
              </v-row>
            </v-card-title>
            <v-divider />
            <v-card-text>
              <v-row>
                <v-col cols="12" md="6">
                  <div class="receipt-overview-row">
                    <div class="receipt-label">{{ $t("warehouse.purchase_order") }}:</div>
                    <div class="receipt-value">#{{ editedItem.number || "-" }}</div>
                  </div>
                  <div class="receipt-overview-row">
                    <div class="receipt-label">{{ $t("warehouse.ordering_date") }}:</div>
                    <div class="receipt-value">
                      {{ localeDate(editedItem.document_date) }}
                    </div>
                  </div>
                  <div class="receipt-overview-row receipt-overview-row--warehouse">
                    <div class="receipt-label">{{ $t("routes.magacin") }}:</div>
                    <autocomplete
                      v-model="editedItem.warehouse_id"
                      :items="warehouses"
                      item-text="name"
                      item-value="id"
                      hide-details
                      color="white"
                      :loading="loadingWarehouses"
                    />
                  </div>

                  <div class="receipt-divider"></div>

                  <div class="receipt-note-block">
                    <div class="receipt-label">{{ $t("purchase_order_note") }}:</div>
                    <div class="receipt-note-value">{{ orderNote || "-" }}</div>
                  </div>
                </v-col>
                <v-col cols="12" md="6">
                  <div class="receipt-overview-row">
                    <div class="receipt-label">{{ $t("supplier") }}:</div>
                    <div class="receipt-value">{{ supplierName || "-" }}</div>
                  </div>
                  <div class="receipt-overview-row receipt-overview-row--field">
                    <div class="receipt-label">
                      {{ $t("warehouse.supplier_dispatch_note") }}:
                    </div>
                    <text-field
                      v-model="dispatcherNumber"
                      hide-details
                      color="white"
                      dense
                    />
                  </div>
                  <div class="receipt-overview-row receipt-overview-row--field">
                    <div class="receipt-label">{{ $t("warehouse.delivery_date") }}:</div>
                    <date-picker
                      v-model="deliveryDate"
                      single-date
                      hide-details
                      hide-label
                      color="white"
                    />
                  </div>
                  <div class="receipt-overview-row receipt-overview-row--field">
                    <div class="receipt-label">{{ $t("warehouse.received_by") }}:</div>
                    <autocomplete
                      v-model="editedItem.received_by"
                      :items="users"
                      hide-details
                      return-object
                      item-text="fullName"
                      item-value="id"
                      required
                      :rules="[rules.required]"
                      color="white"
                      dense
                    />
                  </div>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <v-card outlined>
            <v-card-title>{{ $t("receipt_summary") }}</v-card-title>
            <v-divider />
            <v-card-text class="px-6 py-5">
              <div class="d-flex justify-space-between align-center body-2 mb-4">
                <span class="grey--text text--darken-1"
                  >{{ $t("warehouse.items_count") }}:</span
                >
                <strong>{{ receiptItemsCount }}</strong>
              </div>
              <div class="d-flex justify-space-between align-center body-2 mb-4">
                <span class="grey--text text--darken-1"
                  >{{ $t("warehouse.total_quantity") }}:</span
                >
                <strong>{{
                  formatQuantity(totalEnteredQuantity, summaryMeasureLabel)
                }}</strong>
              </div>
              <div class="d-flex justify-space-between align-center body-2">
                <span class="grey--text text--darken-1"
                  >{{ $t("warehouse.total_amount") }}:</span
                >
                <strong>{{ formatMoney(totalReceiptValue) }}</strong>
              </div>
            </v-card-text>
            <v-card-actions class="pa-4">
              <v-btn
                color="primary"
                depressed
                block
                large
                class="text-none"
                :disabled="saveDisabled"
                :loading="loading"
                @click="finishReceipt"
              >
                <v-icon left>mdi-check-circle-outline</v-icon>
                {{ $t("finish_entry") }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </div>

        <v-card outlined class="receipt-card receipt-table-card">
          <v-card-title>
            <v-spacer />

            <v-btn
              depressed
              color="primary"
              class="text-none font-weight-medium"
              :disabled="!hasPendingGroceries"
              @click="fillRemainingQuantities"
            >
              <v-icon left small>mdi-check-circle-outline</v-icon>
              {{ $t("warehouse.all_ordered_delivered") }}
            </v-btn>
          </v-card-title>
          <v-card-text>
            <v-data-table
              :headers="tableHeaders"
              :items="filteredGroceries"
              item-key="id"
              hide-default-footer
              class="receipt-table"
              :item-class="getReceiptRowClass"
            >
              <template v-slot:item.status="{ item }">
                <div class="d-inline-flex align-center">
                  <v-icon small :color="isFullyDelivered(item) ? '#43a047' : '#fb8c00'">
                    {{ isFullyDelivered(item) ? "mdi-check" : "mdi-circle-slice-8" }}
                  </v-icon>
                </div>
              </template>

              <template v-slot:item.code="{ item }">
                {{ item.code || "-" }}
              </template>

              <template v-slot:item.supplierCode="{ item }"> </template>

              <template v-slot:item.name="{ item }">
                <span class="font-weight-medium">{{ item.name || "-" }}</span>
              </template>

              <template v-slot:item.measure="{ item }">
                {{ item.measure || "-" }}
              </template>

              <template v-slot:item.amount="{ item }">
                <div class="text-right">{{ formatPlainQuantity(item.amount) }}</div>
              </template>

              <template v-slot:item.delivered="{ item }">
                <div class="text-right">
                  {{ formatPlainQuantity(getGroceryDelivered(item)) }}
                </div>
              </template>

              <template v-slot:item.order="{ item }">
                <div class="d-flex justify-end">
                  <text-field
                    v-if="!isFullyDelivered(item)"
                    v-model="item.order"
                    type="number"
                    :min="0"
                    :max="getRemaining(item)"
                    hide-details
                    dense
                    class="receipt-amount-input"
                    @input="normalizeQuantityInput(item)"
                    @blur="normalizeQuantityInput(item)"
                  />
                  <v-icon v-else color="green darken-1">mdi-check-circle</v-icon>
                </div>
              </template>

              <template v-slot:item.gross="{ item }">
                <div class="text-right">
                  {{ formatOptionalQuantity(getCurrentGrossQuantity(item)) }}
                </div>
              </template>

              <template v-slot:item.value="{ item }">
                <div class="text-right font-weight-bold">
                  {{ formatOptionalMoney(getCurrentLineValue(item)) }}
                </div>
              </template>

              <template v-slot:item.unit_price="{ item }">
                <div class="text-right">
                  {{ formatOptionalMoney(getCurrentUnitPrice(item)) }}
                </div>
              </template>

              <template v-slot:body.append>
                <tr class="receipt-table-total-row">
                  <td colspan="4"></td>
                  <td class="text-right font-weight-medium">{{ $t("total") }}</td>
                  <td class="text-right">
                    {{ formatPlainQuantity(totalOrderedQuantity) }}
                  </td>
                  <td class="text-right">
                    {{ formatOptionalQuantity(totalGrossQuantity) }}
                  </td>
                  <td class="text-right">
                    {{ formatPlainQuantity(totalPreviouslyDeliveredQuantity) }}
                  </td>
                  <td class="text-right">
                    {{ formatPlainQuantity(totalEnteredQuantity) }}
                  </td>
                  <td></td>
                  <td class="text-right font-weight-bold">
                    {{ formatOptionalMoney(totalReceiptValue) }}
                  </td>
                </tr>
              </template>
            </v-data-table>

            <v-row class="pt-4" dense>
              <v-col cols="12" md="6">
                <div class="d-flex align-center body-1">
                  <span class="mr-2">{{ $t("warehouse.total_quantity") }}:</span>
                  <strong>{{
                    formatQuantity(totalEnteredQuantity, summaryMeasureLabel)
                  }}</strong>
                </div>
              </v-col>
              <v-col cols="12" md="6">
                <div class="d-flex align-center justify-md-end body-1">
                  <span class="mr-2">{{ $t("warehouse.total_amount") }}:</span>
                  <strong>{{ formatMoney(totalReceiptValue) }}</strong>
                </div>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <v-card outlined>
          <v-card-title class="pb-0">{{ $t("entry_note") }}</v-card-title>
          <v-divider />
          <v-card-text>
            <text-area v-model="note" hide-details color="white" />
          </v-card-text>
        </v-card>
      </div>
    </v-form>
  </fullscreen-dialog>
</template>

<script>
import hasModel from "@/mixins/hasModel";
import hasSnackbar from "@/mixins/hasSnackbar";
import hasPrinting from "@/mixins/hasPrinting";
import hasValidation from "@/mixins/hasValidation";
import hasWarehouseUsers from "@/mixins/warehouse/hasWarehouseUsers";
import FullscreenDialog from "@/components/dialogs/FullscreenDialog.vue";
import { orderDocumentGrocery } from "@/models/warehouse";
import warehouseService from "@/services/warehouse";

export default {
  name: "WarehousePurchaseReceiptDialog",
  mixins: [hasModel, hasValidation, hasSnackbar, hasPrinting, hasWarehouseUsers],
  components: {
    FullscreenDialog,
  },
  props: {
    editedItem: {
      type: Object,
      default: orderDocumentGrocery,
    },
  },
  data() {
    return {
      valid: false,
      loading: false,
      note: "",
      orderNote: "",
      dispatcherNumber: "",
      entryGoodsNumber: "",
      deliveryDate: null,
      search: "",
      warehouses: [],
      loadingWarehouses: false,
    };
  },
  methods: {
    async fetchWarehouses() {
      if (this.loadingWarehouses) return;

      this.loadingWarehouses = true;

      try {
        const response = await warehouseService.getWarehouses();
        this.warehouses = response.data || [];
      } catch (error) {
        this.showErrorSnackbar(error, this.$t("error_loading_warehouses"));
      } finally {
        this.loadingWarehouses = false;
      }
    },
    saveReceipt() {
      this.save();
    },
    finishReceipt() {
      this.save();
    },
    fillRemainingQuantities() {
      (this.editedItem.groceries || []).forEach((grocery) => {
        if (!this.isFullyDelivered(grocery)) {
          this.$set(grocery, "order", this.getRemaining(grocery) || null);
          this.$set(
            grocery,
            "gross_quantity",
            Number(grocery.gross_quantity) || grocery.order || 0
          );
        }
      });
    },
    initializeReceiptDefaults() {
      (this.editedItem.groceries || []).forEach((grocery) => {
        const remainingAmount = this.getRemaining(grocery);

        if (
          (grocery.order === null ||
            grocery.order === undefined ||
            grocery.order === "") &&
          remainingAmount > 0
        ) {
          this.$set(grocery, "order", remainingAmount);
        }

        if (
          (grocery.gross_quantity === null ||
            grocery.gross_quantity === undefined ||
            grocery.gross_quantity === "") &&
          Number(grocery.order) > 0
        ) {
          this.$set(grocery, "gross_quantity", Number(grocery.order));
        }

        if (
          (!Number(grocery.unit_price) || Number(grocery.unit_price) <= 0) &&
          Number(grocery.total_amount) > 0 &&
          Number(grocery.amount) > 0
        ) {
          this.$set(
            grocery,
            "unit_price",
            Number(grocery.total_amount) / Number(grocery.amount)
          );
        }
      });
    },
    normalizeQuantityInput(item) {
      const value = Number(item.order);

      if (!Number.isFinite(value) || value <= 0) {
        item.order = value === 0 ? 0 : null;
        return;
      }

      item.order = Math.min(value, this.getRemaining(item));
    },
    syncReceiptFields() {
      this.editedItem.delivery_date = this.deliveryDate;
      this.editedItem.document_number = this.entryGoodsNumber || this.dispatcherNumber;
      this.editedItem.supplier_invoice_number = this.dispatcherNumber;
      this.editedItem.notes = this.note;
    },
    getSelectedUserId(user) {
      if (!user) {
        return null;
      }

      return typeof user === "object" ? user.id : user;
    },
    getSelectedUserName(user) {
      if (!user) {
        return "";
      }

      if (typeof user === "object") {
        return user.fullName || [user.name, user.lastname].filter(Boolean).join(" ");
      }

      const matchedUser = (this.users || []).find((item) => item.id === user);
      return matchedUser?.fullName || "";
    },
    getReceiptGroceries() {
      return (this.editedItem.groceries || [])
        .map((item) => ({
          grocery_id: item.id,
          warehouse_id: this.editedItem.warehouse_id,
          amount: Number(item.order) || 0,
          measure: item.measure,
          transaction_type: "entry",
          notes: this.note,
          code: item.code,
          name: item.name,
        }))
        .filter((item) => item.amount > 0);
    },
    getCurrentReceiptAmount(grocery) {
      return Math.max(Number(grocery.order) || 0, 0);
    },
    getNextStatus() {
      const groceries = this.editedItem.groceries || [];
      const hasAnyDelivered = groceries.some(
        (grocery) =>
          this.getGroceryDelivered(grocery) + this.getCurrentReceiptAmount(grocery) > 0
      );

      if (!hasAnyDelivered) {
        return null;
      }

      const allDelivered = groceries.every(
        (grocery) =>
          this.getRemaining(grocery) - this.getCurrentReceiptAmount(grocery) <= 0
      );

      return allDelivered ? "delivered" : "delivered-half";
    },
    async save() {
      const isValid = this.$refs.form ? this.$refs.form.validate() : true;
      const groceries = this.getReceiptGroceries();

      if (
        !isValid ||
        !groceries.length ||
        !this.receivedById ||
        !this.editedItem.warehouse_id
      ) {
        return;
      }

      this.syncReceiptFields();
      this.loading = true;

      const params = {
        ...this.editedItem,
        document_type: "entry",
        transaction_type: "entry",
        parent_id: this.editedItem.id,
        warehouse_id: this.editedItem.warehouse_id,
        received_by: this.receivedById,
        delivery_date: this.deliveryDate,
        shipping_date: this.deliveryDate,
        document_number: this.dispatcherNumber,
        supplier_invoice_number: this.dispatcherNumber,
        notes: this.note,
        groceries,
      };

      try {
        await this.handleRequest(warehouseService.addDocumentTransaction(params), {
          successMessage: this.$t("warehouse_entry_created"),
          errorMessagePrefix: this.$t("error_creating_warehouse_entry") + ": ",
        });

        const status = this.getNextStatus();

        if (status) {
          try {
            await warehouseService.patchWarehouse(this.editedItem.id, {
              data: { status },
            });
            this.showSuccessSnackbar(this.$t("warehouse_status_updated"));
          } catch (error) {
            this.showErrorSnackbar(this.$t("error_updating_warehouse_status"));
          }
        }

        this.model = false;
        this.$emit("success");
      } catch (error) {
        console.error("Failed to save invoice", error);
      } finally {
        this.loading = false;
      }
    },
    print() {
      this.syncReceiptFields();

      const deliveredDate =
        this.$t("warehouse.shipping_date") +
        ":" +
        this.localeDate(this.deliveryDate || new Date());
      const orderDate =
        this.$t("warehouse.ordering_date") +
        ":" +
        this.localeDate(this.editedItem.document_date);
      const orderNumber =
        this.$t("warehouse.order_number") + ": " + this.editedItem.number;
      const deliveredNumber =
        this.$t("warehouse.invoice_number") + ": " + this.dispatcherNumber;
      const note = this.note
        ? `
            <div style="font-size: 12px;margin-top: 20px;">
            ${this.$t("note")}: ${this.note}
            </div>
          `
        : "";

      this.printDocument({
        title: this.$t("warehouse.entry_sheet") + ` #${this.editedItem.number}`,
        hideDatetime: true,
        beforeTitleHtml: `
            <div style="display: flex;justify-content: space-between;font-size: 16px;margin-top: 20px;">
              <div>${orderNumber}</div>
              <div>${deliveredNumber}</div>
            </div>
            <div style="display: flex;justify-content: space-between;font-size: 16px;">
              <div>${orderDate}</div>
              <div>${deliveredDate}</div>
            </div>
          `,
        company1: {
          id: this.editedItem.customer?.id || 101,
          name: this.toLatin(
            [this.editedItem.customer?.name, this.editedItem.customer?.lastname].join(
              " "
            ) || ""
          ),
          city: this.toLatin(this.editedItem.customer?.city || ""),
          address: this.toLatin(this.editedItem.customer?.address || ""),
          phone: this.translate(this.editedItem.customer?.phone || ""),
          email: this.translate(this.editedItem.customer?.email || ""),
          printTitle: this.$t("warehouse.orderer"),
        },
        company2: {
          ...this.editedItem.supplier,
          printTitle: this.$t("supplier"),
        },
        signature: {
          left: {
            title: this.$t("warehouse.product_delivered_by"),
            name: this.toLatin(this.editedItem.supplier?.name || ""),
          },
          right: {
            title: this.$t("warehouse.received_by"),
            name: this.toLatin(this.getSelectedUserName(this.editedItem.received_by)),
          },
        },
        tables: [
          {
            headers: [
              ...this.headers,
              { text: this.$t("unit_of_measurment"), value: "measure" },
              { text: this.$t("price"), value: "price", align: "right" },
              { text: this.$t("amount"), value: "amount", align: "right" },
            ],
            items: this.printItems.map((item, index) => ({
              ...item,
              num: index + 1,
              name: this.toLatin(item.name),
              price: "",
              amount: item.amount,
            })),
          },
        ],
        afterTableHtml: note,
      });
    },
    getGroceryDelivered(grocery) {
      return this.getGroceryTransactions(grocery).reduce(
        (total, transaction) => total + Number(transaction.amount),
        0
      );
    },
    getRemaining(grocery) {
      return Math.max(Number(grocery.amount) - this.getGroceryDelivered(grocery), 0);
    },
    isFullyDelivered(grocery) {
      return this.getRemaining(grocery) <= 0;
    },
    getGroceryTransactions(grocery) {
      return this.documentTransactions.filter(
        (transaction) => transaction.grocery_id === grocery.id
      );
    },
    getCurrentGrossQuantity(grocery) {
      return Number(grocery.gross_quantity) || this.getCurrentReceiptAmount(grocery);
    },
    getCurrentUnitPrice(grocery) {
      return Number(grocery.unit_price) || 0;
    },
    getCurrentLineValue(grocery) {
      const qty = this.isFullyDelivered(grocery)
        ? this.getGroceryDelivered(grocery)
        : this.getCurrentReceiptAmount(grocery);
      return qty * this.getCurrentUnitPrice(grocery);
    },
    formatMoney(amount) {
      return `${(Number(amount) || 0).toFixed(2)} ${this.restaurantCurrency}`;
    },
    formatPlainQuantity(amount) {
      return Number(amount) || 0;
    },
    formatOptionalQuantity(amount) {
      const normalizedAmount = Number(amount) || 0;
      return normalizedAmount === 0 ? "" : normalizedAmount;
    },
    formatQuantity(amount, measure) {
      return measure ? `${Number(amount) || 0} ${measure}` : Number(amount) || 0;
    },
    formatOptionalMoney(amount) {
      return Number(amount) === 0 ? "" : this.formatMoney(amount);
    },
    getReceiptRowClass(item) {
      return this.isFullyDelivered(item)
        ? "receipt-table-row receipt-table-row--completed"
        : "receipt-table-row";
    },
  },
  computed: {
    supplierName() {
      return this.editedItem.supplier?.name || this.editedItem.supplier_id?.name || "";
    },
    ordererName() {
      return this.getSelectedUserName(this.editedItem.ordered_by);
    },
    receivedById() {
      return this.getSelectedUserId(this.editedItem.received_by);
    },
    totalGroceries() {
      return (this.editedItem.groceries || []).length;
    },
    hasPendingGroceries() {
      return (this.editedItem.groceries || []).some(
        (grocery) => this.getRemaining(grocery) > 0
      );
    },
    hasEnteredQuantities() {
      return (this.editedItem.groceries || []).some(
        (grocery) => this.getCurrentReceiptAmount(grocery) > 0
      );
    },
    totalEnteredQuantity() {
      return (this.editedItem.groceries || []).reduce(
        (total, grocery) => total + this.getCurrentReceiptAmount(grocery),
        0
      );
    },
    totalOrderedQuantity() {
      return (this.editedItem.groceries || []).reduce(
        (total, grocery) => total + (Number(grocery.amount) || 0),
        0
      );
    },
    totalPreviouslyDeliveredQuantity() {
      return (this.editedItem.groceries || []).reduce(
        (total, grocery) => total + this.getGroceryDelivered(grocery),
        0
      );
    },
    totalGrossQuantity() {
      return (this.editedItem.groceries || []).reduce(
        (total, grocery) => total + this.getCurrentGrossQuantity(grocery),
        0
      );
    },
    totalReceiptValue() {
      return (this.editedItem.groceries || []).reduce(
        (total, grocery) => total + this.getCurrentLineValue(grocery),
        0
      );
    },
    receiptItemsCount() {
      return (
        (this.editedItem.groceries || []).filter(
          (grocery) =>
            this.getCurrentReceiptAmount(grocery) > 0 || this.isFullyDelivered(grocery)
        ).length || this.totalGroceries
      );
    },
    summaryMeasureLabel() {
      const uniqueMeasures = [
        ...new Set(
          (this.editedItem.groceries || []).map((item) => item.measure).filter(Boolean)
        ),
      ];

      return uniqueMeasures.length === 1 ? uniqueMeasures[0] : "";
    },
    allDelivered() {
      const groceries = this.editedItem.groceries || [];
      return groceries.length > 0 && groceries.every((g) => this.isFullyDelivered(g));
    },
    statusText() {
      return this.allDelivered
        ? this.$t("status_delivered").toUpperCase()
        : this.$t("status_in_progress").toUpperCase();
    },
    saveDisabled() {
      return (
        !this.valid ||
        !this.receivedById ||
        !this.editedItem.warehouse_id ||
        !this.hasEnteredQuantities ||
        this.loading
      );
    },
    receiptTitle() {
      return `${this.$t("receipt_title")} #${this.editedItem?.id || ""}`.trim();
    },
    filteredGroceries() {
      const searchLower = this.translate((this.search || "").toLowerCase());

      return [...(this.editedItem.groceries || [])]
        .filter((item) => {
          if (!searchLower) {
            return true;
          }

          return (
            this.translate((item.name || "").toLowerCase()).includes(searchLower) ||
            String(item.code || "")
              .toLowerCase()
              .includes(searchLower)
          );
        })
        .sort((a, b) => {
          const aComplete = this.isFullyDelivered(a);
          const bComplete = this.isFullyDelivered(b);

          if (aComplete !== bComplete) {
            return aComplete ? 1 : -1;
          }

          const remainingDiff = this.getRemaining(b) - this.getRemaining(a);

          if (remainingDiff !== 0) {
            return remainingDiff;
          }

          return (a.name || "").localeCompare(b.name || "");
        });
    },
    documentTransactions() {
      let transactions = [];

      (this.editedItem.children || []).forEach((child) => {
        transactions = transactions.concat(
          (child.transactions || []).filter(
            (transaction) => transaction.transaction_type === "entry"
          )
        );
      });

      return transactions;
    },
    printItems() {
      const receiptItems = (this.editedItem.groceries || []).filter(
        (item) => this.getCurrentReceiptAmount(item) > 0
      );
      const items = receiptItems.length ? receiptItems : this.editedItem.groceries || [];

      return items.map((item) => ({
        ...item,
        amount: receiptItems.length ? this.getCurrentReceiptAmount(item) : item.amount,
      }));
    },
    headers() {
      return [
        { text: this.$t("r_b"), value: "num" },
        { text: this.$t("code"), align: "start", value: "code" },
        { text: this.$t("title"), align: "start", value: "name" },
        { text: this.$t("warehouse.ordered"), align: "start", value: "amount" },
        {
          text: this.$t("warehouse.delivered"),
          align: "start",
          value: "order",
          sortable: false,
        },
      ];
    },
    tableHeaders() {
      return [
        {
          text: "",
          value: "status",
          sortable: false,
          align: "center",
          width: 56,
        },
        { text: this.$t("code"), value: "code", width: 90 },
        { text: this.$t("supplier_code"), value: "supplierCode", width: 110 },
        { text: this.$t("warehouse.product"), value: "name" },
        {
          text: this.$t("unit_of_measurment"),
          value: "measure",
          width: 270,
          align: "right",
        },
        {
          text: this.$t("warehouse.ordered"),
          value: "amount",
          align: "end",
          width: 110,
        },
        {
          text:
            this.$t("warehouse.ordered") + ` (${this.$t("warehouse.quantity_gross")})`,
          value: "gross",
          align: "end",
          sortable: false,
          width: 150,
        },
        {
          text: this.$t("previously_delivered"),
          value: "delivered",
          align: "end",
          sortable: false,
          width: 150,
        },
        {
          text: this.$t("warehouse.delivered_now"),
          value: "order",
          align: "end",
          sortable: false,
          width: 170,
        },
        {
          text: this.$t("price"),
          value: "unit_price",
          align: "end",
          sortable: false,
          width: 120,
        },
        {
          text: this.$t("amount"),
          value: "value",
          align: "end",
          sortable: false,
          width: 120,
        },
      ];
    },
    documentTypes() {
      return {
        order: this.$t("warehouse.purchase_order"),
        request: this.$t("warehouse.request_quote"),
        entry: this.$t("warehouse.entry"),
      };
    },
  },
  watch: {
    model(newVal) {
      if (newVal) {
        this.fetchUsers();
        this.fetchWarehouses();
        this.deliveryDate =
          this.editedItem.delivery_date || this.editedItem.document_date || null;
        this.orderNote = this.editedItem.notes || "";
        this.dispatcherNumber = this.editedItem.supplier_invoice_number || "";
        this.entryGoodsNumber = this.editedItem.document_number || "";
        this.note = this.editedItem.notes || "";
        this.initializeReceiptDefaults();
      }
    },
  },
};
</script>

<style scoped>
.receipt-card {
  border-radius: 4px;
  border-color: #e8e2db !important;
  background: #fff !important;
  box-shadow: none;
}

.receipt-layout {
  display: grid;
  gap: 16px;
  min-height: calc(100vh - 156px);
}

.receipt-top-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 332px;
  gap: 16px;
}

.receipt-overview-row {
  display: grid;
  grid-template-columns: 180px minmax(0, 1fr);
  align-items: center;
  gap: 10px;
  min-height: 40px;
}

.receipt-overview-row + .receipt-overview-row {
  margin-top: 6px;
}

.receipt-divider {
  margin: 14px 0 16px;
  height: 1px;
  background: #ede8e1;
}

.receipt-label {
  font-size: 0.92rem;
  color: #75685d;
}

.receipt-value {
  font-size: 0.96rem;
  font-weight: 700;
  color: #2b241e;
}

.receipt-note-block {
  display: grid;
  gap: 6px;
}

.receipt-note-value {
  color: #4a3f36;
  line-height: 1.45;
  white-space: pre-wrap;
}

.receipt-table-card {
  overflow: hidden;
}

.receipt-table {
  border: 1px solid #eee6dd;
  border-radius: 4px;
  overflow: hidden;
}

.receipt-table ::v-deep .v-data-table__wrapper {
  overflow-x: auto;
}

.receipt-table ::v-deep table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
}

.receipt-table ::v-deep thead th {
  background: #fff;
  color: #716559;
  font-size: 0.85rem;
  font-weight: 700;
  border-bottom: 1px solid #eee6dd !important;
  padding: 12px 10px;
}

.receipt-table ::v-deep tbody td {
  padding: 10px;
  border-bottom: 1px solid #f1ebe4 !important;
  font-size: 0.95rem;
  color: #342c25;
  background: #fff;
}

.receipt-table ::v-deep .receipt-table-row--completed td {
  background: #f5f8f2;
}

.receipt-amount-input {
  max-width: 96px;
}

.receipt-table-total-row td {
  color: #5b4f43;
}

@media (max-width: 1264px) {
  .receipt-top-row {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 960px) {
  .receipt-overview-row {
    grid-template-columns: 1fr;
    gap: 6px;
  }
}
</style>
